import React, { useCallback, useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ContentTemplate,
  Grid,
  HeaderTemplate,
  KeyValue,
  MUIDataTable,
  MUIDataTableColumn,
  SearchBar,
  Stepper,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  Typography,
  useDataTable,
} from 'web-library';
import { useHistory, useLocation, useParams } from 'react-router-dom';


import { makeStyles } from 'tss-react/mui';
import HeaderWithPill from 'web-library/stories/atom/HeaderWithPill/HeaderWithPill';
import { useGetGroupRegistrationSummary } from '../../../api-refactored/group-exam-registration/useGetRegistrationSummary';
import {
  GroupExamOptionalPaper,
  PostRetrieveCandidateDetailsResponse,
  usePostRetrieveCandidateDetails,
} from '../../../api-refactored/group-exam-registration/usePostRetrieveCandidateDetails';
import RouteKey from '../../../routes/key';
import GroupExamRegistrationTitleHeader from '../../../components/GroupExamRegistrationTitleHeader';
import DraftTimeStamp from '../../../components/DraftTimeStamp';
import {typographyTheme} from "web-library/theme/helper/tssHelper"

interface CoordinatorReviewCandidateDetailsPageProps {
  routeSteps: { label: string }[];
}

const useStyles = makeStyles()((theme) =>
({
  root: {
    '& .MuiPaper-root.MuiStepper-root': {
      [theme.breakpoints.up('md')]: {
        marginLeft: '-70px',
      },
    },
    '& .MuiTableRow-root': {
      verticalAlign: 'top',
    },
    '& .MuiTableBody-root': {
      '& .MuiTableCell-root.MuiTableCell-body div': {
         ...typographyTheme(theme, 'body2'),
      },
    },
  },
  dataTable: {
     ...typographyTheme(theme, 'body2'),
  },
  searchInput: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(3),
  },
  optionalPaper: {
     ...typographyTheme(theme, 'subtitle2'),
    color: theme.color.text.textSecondary,
  },
  buttonRow: {
    paddingTop: 0,
  },
}));

interface IReviewCandidateDetailsColumn {
  dataContent: PostRetrieveCandidateDetailsResponse[];
  classes: any;
  t: TFunction<'translation'>;
}

const renderOptionalPaper = (optionalPapers: GroupExamOptionalPaper[], t: TFunction<string>) => {
  const optionalPapersOptions = optionalPapers
    .map((paper) => `${t('exam->>ReviewExam.Paper')} ${paper.paperNo}`)
    .join(', ');
  return `+ ${t('exam->>ReviewExam.Optional Papers')}: ${optionalPapersOptions}`;
};

const reviewCandidateDetailsColumn = ({
  dataContent,
  t,
  classes,
}: IReviewCandidateDetailsColumn): MUIDataTableColumn[] => [
    {
      name: 'uinFinPassport',
      label: t('exam->>ReviewExam.NRIC/FIN/Passport No.'),
      options: {
        setCellProps: () => ({ style: { width: '20%' } }),
      },
    },
    {
      name: 'candidateName',
      label: t('exam->>ReviewExam.CandidateName'),
    },
    {
      name: 'examLevel',
      label: t('exam->>ReviewExam.Exam Level'),
      options: {
        setCellProps: () => ({ style: { width: '20%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography className={classes.dataTable}>
            {dataContent[dataIndex].examLevel.join(', ')}
          </Typography>
        ),
      },
    },
    {
      name: 'subjectLists',
      label: t('exam->>ReviewExam.Subjects'),
      options: {
        setCellProps: () => ({ style: { width: '35%' } }),
        customBodyRenderLite: (dataIndex: number) =>
          dataContent[dataIndex].subjectLists.map((subject) => (
            <>
              <Typography className={classes.dataTable}>
                {subject.subjectName} ({subject.subjectCode})
              </Typography>
              {subject.optionalPapers.length > 0 && (
                <Typography variant="body2" className={classes.optionalPaper}>
                  {renderOptionalPaper(subject.optionalPapers, t)}
                </Typography>
              )}
            </>
          )),
      },
    },
  ];

const { exam } = RouteKey.child;

const {
  coordinatorExamRegistrations,
  bulkUploadCandidates,
  coordinatorReviewExam,
} = RouteKey.exam.child;

const CoordinatorReviewCandidateDetails = ({
  routeSteps,
}: CoordinatorReviewCandidateDetailsPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { uuid: applicationUuid } = useParams<any>();
  const { state: tokenState } = useLocation<{ token: string }>();
  const { classes } = useStyles();
  const { data: summaryData } = useGetGroupRegistrationSummary({
    uuid: applicationUuid,
    refetchInterval: undefined,
  });

  const { mutate: mutatePostRetrieveCandidateDetails } = usePostRetrieveCandidateDetails({
    onSuccess: (data: { content: any; totalElements: any }) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });
  const { state, dispatch: tableDispatch } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: '',
    order: 'asc',
    size: 5,
    page: 0,
    search: '',
    filters: {},
  });

  const retrieveCandidateDetails = useCallback(() => {
    mutatePostRetrieveCandidateDetails({
      q: state.search || '',
      applicationUuid,
      apiPathParam: {
        order: state.order,
        page: state.page,
        pageSize: state.size,
      },
    });
  }, [
    state.order,
    state.page,
    state.size,
    state.search,
    applicationUuid,
    mutatePostRetrieveCandidateDetails,
  ]);

  useEffect(() => {
    retrieveCandidateDetails();
  }, [retrieveCandidateDetails]);

  return (
    <Box className={classes.root}>
      <GroupExamRegistrationTitleHeader
        examRegistrationType={summaryData?.examRegistrationType}
        t={t}
      />
      <HeaderTemplate>
        <KeyValue
          keyText={t('exam->>RegisterExamInstructions.Exam Level')}
          valueText={summaryData?.examType.description || ''}
        />
        <Stepper activeStep={1} steps={routeSteps} />
      </HeaderTemplate>
      <ContentTemplate type="full">
        <HeaderWithPill
          title={t('exam->>ReviewExam.ViewCandidateDetails')}
          pillValue={state.count.toString() || '0'}
        />
        <Box id="candidate-listing">
          <SearchBar
            className={classes.searchInput}
            placeholder={t('exam->>ReviewExam.SearchCandidatePlaceholder')}
            name="searchInput"
            ariaLabel="searchInput"
            defaultValue={state.search}
            onSearch={(searchStr: string) => {
              tableDispatch({
                type: 'UPDATE_SEARCH',
                payload: searchStr.trim(),
              });
            }}
          />
          <MUIDataTable
            columns={reviewCandidateDetailsColumn({
              dataContent: state.data,
              t,
              classes,
            })}
            data={state.data}
            state={state}
            dispatch={tableDispatch}
            options={{
              pagination: true,
            }}
          />
        </Box>
      </ContentTemplate>
      <ContentTemplate continuous>
        <Grid
          className={classes.buttonRow}
          container
          direction="row"
          alignItems="flex-start"
          spacing={3}
        >
          <Grid xs={12} md={3}>
            <Button
              id="button-reviewCandidates-back-to-bulkUpload"
              fullWidth
              // className={classes.button}
              variant="outlined"
              onClick={() =>
                history.push(
                  `/${exam}/${coordinatorExamRegistrations.route}/${applicationUuid}/${bulkUploadCandidates.route}`,
                  { token: tokenState && tokenState.token }
                )
              }
            >
              {t('exam->>UploadExcelTemplate.Back')}
            </Button>
          </Grid>
          <Grid xs={12} md={4}>
            <Button
              id="button-bulkUpload-proceed-to-viewCandidateList"
              fullWidth
              // className={classes.button}
              onClick={() =>
                history.push(
                  `/${exam}/${coordinatorExamRegistrations.route}/${applicationUuid}/${coordinatorReviewExam.route}`
                )
              }
            >
              {t('exam->>ReviewExam.ReviewRegistration')}
            </Button>
          </Grid>
        </Grid>
        {summaryData && <DraftTimeStamp savedAt={summaryData.lastUpdatedTime} />}
      </ContentTemplate>
    </Box>
  );
};

export default CoordinatorReviewCandidateDetails;
